import React from "react";

import MarcEngine from "../components/ScorePanel/marcEngine";
import { CELEBRATION_MS } from "../constants";
import { usePalette } from "../contexts/PaletteContext";
import { useTool } from "../contexts/ToolContext";
import { scoreDrawing } from "../scoring";
import { playCommit } from "../sound";

// Binding hook for MarcEngine (per-marc play state). Pushes React-side
// inputs into the engine via update(), subscribes to engine emits and
// snapshots into React state, and bridges engine events back to
// React-side setters (auto-color on target change; auto-revert to pen
// on celebration end).
//
// `active` should be true only during speedrun mode. When false the
// engine is reset and update() calls are skipped — this keeps committed
// flags from carrying over across mode switches without needing the
// hook itself to mount/unmount. The engine also resets when `marcId`
// changes, since region IDs collide across marcs and stale committed
// flags would mis-apply to the next marc's regions.

export default function useMarcEngine({
  active,
  marcId,
  strokes,
  palette,
  reference,
  scoreOverrides,
  lastTool,
  lastActionAt,
}) {
  const { setTool } = useTool();
  const { setActiveColorIndex } = usePalette();

  const engineRef = React.useRef(null);
  if (engineRef.current == null) {
    // setActiveColorIndex / setTool are useState setters (stable
    // identity for the provider's lifetime) so capturing them at
    // construction is safe — they don't go stale across renders.
    engineRef.current = new MarcEngine({
      celebrationMs: CELEBRATION_MS,
      onTargetChange: (region) => {
        if (region?.colorIndex != null) {
          setActiveColorIndex(region.colorIndex);
        }
      },
      onCelebrationStart: () => playCommit(),
      onCelebrationEnd: () => setTool("pen"),
    });
  }

  // Score is a pure derivation over committed strokes. Only meaningful
  // when active + reference loaded; empty otherwise.
  const result = React.useMemo(() => {
    if (!active || !reference) {
      return { regions: [], overall: 0, labeledCount: 0, detectedCount: 0 };
    }
    return scoreDrawing({
      strokes,
      palette,
      reference,
      overrides: scoreOverrides,
    });
  }, [active, strokes, palette, reference, scoreOverrides]);

  const [snapshot, setSnapshot] = React.useState(() =>
    engineRef.current.getSnapshot(),
  );

  const recompute = React.useCallback(() => {
    setSnapshot(engineRef.current.getSnapshot());
  }, []);

  React.useEffect(() => {
    const engine = engineRef.current;
    const unsub = engine.subscribe(recompute);
    return () => {
      unsub();
      engine.dispose();
    };
  }, [recompute]);

  // Reset on inactive (mode switch out) or marcId change (round
  // advance, or debug arrow-key cycle).
  React.useEffect(() => {
    engineRef.current.reset();
  }, [active, marcId]);

  // Push React-side inputs into the engine. Skip while inactive so
  // stroke/score churn during author mode doesn't drive the state
  // machine.
  React.useEffect(() => {
    if (!active) return;
    engineRef.current.update({
      regions: result.regions,
      lastTool,
      fillMs: scoreOverrides.detectionFillMs,
      strokeMs: scoreOverrides.detectionStrokeMs,
      celebrationMs: CELEBRATION_MS,
    });
  }, [
    active,
    result.regions,
    lastTool,
    lastActionAt,
    scoreOverrides.detectionFillMs,
    scoreOverrides.detectionStrokeMs,
  ]);

  const bindBar = React.useCallback((el) => {
    engineRef.current.bindBar(el);
  }, []);

  return { snapshot, bindBar };
}
